import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import SectionHeading from './SectionHeading';
import AutoScroller from './AutoScroller';
import Icon from './Icon';
import { articles } from '../content/blog';
import { blogIntro } from '../content/pages';

/**
 * Анонс блогу на головній.
 *
 * Раніше стаття розгорталася тут повністю і не мала власної адреси. Тепер тут
 * стрічка карток із переходом на статтю; стрічка їде сама (AutoScroller), але
 * лише коли є що прокручувати.
 *
 * Розкладка у дві колонки: ліворуч заголовок і абзац про те, навіщо блог,
 * праворуч картки. Заголовок «Блог» на всю ширину над однією карткою
 * лишав праворуч від нього порожній екран.
 *
 * Картка втратила бурштиновий колір і трикутник «увага»: це були кольори
 * попередження, а стаття — розбір зміни в законодавстві, а не сигнал
 * тривоги. Рубрика й дата говорять усе потрібне.
 */
const ArticleCard = ({ article }) => (
  <Link
    to={`/blog/${article.slug}`}
    className="group relative flex flex-col h-full rounded-2xl border border-slate-200 bg-white p-6 lg:p-8 shadow-sm hover:shadow-md hover:border-teal-300 transition-[box-shadow,border-color] duration-300"
  >
    <div className="flex items-center gap-3 flex-wrap mb-4">
      <span className="inline-flex items-center rounded-full bg-teal-50 text-teal-700 text-[0.7rem] font-semibold uppercase tracking-[0.15em] px-3 py-1">
        {article.tag}
      </span>
      <time dateTime={article.date} className="text-sm text-slate-500">
        {article.dateLabel}
      </time>
    </div>

    <h3 className="text-xl lg:text-2xl font-bold font-display text-slate-900 leading-snug group-hover:text-teal-800 transition-colors duration-200">
      {article.title}
    </h3>

    <p className="mt-3 text-slate-600 leading-relaxed line-clamp-4">{article.excerpt}</p>

    <span className="mt-auto pt-6 inline-flex items-center gap-2 text-teal-700 font-semibold text-sm">
      Читати повністю
      <Icon name="fa-arrow-right" className="transition-transform duration-300 group-hover:translate-x-1" />
    </span>
  </Link>
);

const Blog = () => {
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });

  return (
    <section id="blog" className="py-16 lg:py-24 bg-slate-50" ref={ref}>
      <div className="section-container">
        <div className="grid gap-10 lg:gap-14 lg:grid-cols-12 items-start">
          <motion.div
            className="lg:col-span-4"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <SectionHeading title="Блог" caption="Зміни в законодавстві" />
            <p className="mt-5 text-slate-600 leading-relaxed">{blogIntro}</p>
            <Link
              to="/blog"
              className="mt-6 inline-flex items-center gap-2 text-teal-700 font-semibold hover:text-teal-800 transition-colors duration-200"
            >
              Усі матеріали блогу
              <Icon name="fa-arrow-right" />
            </Link>
          </motion.div>

          <motion.div
            className="lg:col-span-8"
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            <AutoScroller
              tone="muted"
              items={articles}
              itemKey={(article) => article.slug}
              renderItem={(article) => <ArticleCard article={article} />}
            />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Blog;
